import React, { useState, useEffect, useCallback } from 'react';
import { useApp, ROLE_LABELS } from '../../context/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Save, User, Key, Eye, EyeOff, CheckCircle2, AlertTriangle, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';

export function ProfileDialog({ open, onOpenChange }) {
  const { currentUser, api } = useApp();
  const [hiteamEmail, setHiteamEmail] = useState('');
  const [hiteamPassword, setHiteamPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadCredentials = useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const res = await api.getHiteamCredentials(currentUser.id);
      setHiteamEmail(res.data.hiteam_email || '');
      setHasCredentials(res.data.has_credentials);
      setHiteamPassword('');
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [api, currentUser]);

  useEffect(() => {
    if (open) loadCredentials();
  }, [open, loadCredentials]);

  const handleSave = async () => {
    if (!hiteamEmail.trim()) {
      toast.error('HiTeam User ID is required');
      return;
    }
    if (!hiteamPassword.trim() && !hasCredentials) {
      toast.error('HiTeam Password is required');
      return;
    }
    setSaving(true);
    try {
      const payload = { hiteam_email: hiteamEmail.trim() };
      if (hiteamPassword.trim()) {
        payload.hiteam_password = hiteamPassword.trim();
      }
      await api.saveHiteamCredentials(currentUser.id, payload);
      toast.success('HiTeam credentials saved successfully');
      setHasCredentials(true);
      setHiteamPassword('');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save credentials');
    } finally { setSaving(false); }
  };

  if (!currentUser) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-amber-600" /> My Profile
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* User Info */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-lg flex-shrink-0">
              {currentUser.name?.charAt(0) || 'U'}
            </div>
            <div className="overflow-hidden">
              <p data-testid="profile-name" className="text-sm font-semibold text-slate-900 truncate">{currentUser.name}</p>
              <p data-testid="profile-email" className="text-xs text-slate-500 truncate">{currentUser.email}</p>
              <Badge className="text-[10px] mt-0.5 bg-amber-100 text-amber-700">{ROLE_LABELS[currentUser.role] || currentUser.role}</Badge>
            </div>
          </div>

          {/* HiTeam Credentials Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4 text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-800">HiTeam Credentials</h3>
              {hasCredentials && (
                <Badge data-testid="hiteam-configured-badge" className="text-[9px] bg-emerald-100 text-emerald-700">Configured</Badge>
              )}
            </div>
            <p className="text-xs text-slate-500">These credentials are used for POS sales point validation. Enter your HiTeam login ID and password.</p>

            {!hasCredentials && (
              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700">
                  Please save your HiTeam credentials to enable sales point validation. Without them, the system will use default credentials which may not work for your account.
                </p>
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs text-slate-600">HiTeam User ID (Email)</Label>
                  <Input
                    data-testid="hiteam-email-input"
                    type="email"
                    value={hiteamEmail}
                    onChange={e => setHiteamEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    className="mt-1 h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">
                    HiTeam Password {hasCredentials && <span className="text-slate-400">(leave blank to keep current)</span>}
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      data-testid="hiteam-password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={hiteamPassword}
                      onChange={e => setHiteamPassword(e.target.value)}
                      placeholder={hasCredentials ? 'Enter new password to change' : 'Enter password'}
                      className="h-9 pr-9"
                    />
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    >
                      {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>

                <Button
                  data-testid="save-hiteam-credentials-btn"
                  onClick={handleSave}
                  disabled={saving || !hiteamEmail.trim() || (!hiteamPassword.trim() && !hasCredentials)}
                  className="w-full gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? 'Validating & Saving...' : 'Save HiTeam Credentials'}
                </Button>
              </>
            )}

            {hasCredentials && (
              <div className="flex items-center gap-1.5 text-xs text-emerald-600">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Your HiTeam credentials are saved and will be used for sales point validation.
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function HiteamSetupPrompt({ open, onOpenChange, isMandatory = false }) {
  const { currentUser, api } = useApp();
  const [hiteamEmail, setHiteamEmail] = useState('');
  const [hiteamPassword, setHiteamPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!hiteamEmail.trim() || !hiteamPassword.trim()) {
      toast.error('Both fields are required');
      return;
    }
    setSaving(true);
    try {
      await api.saveHiteamCredentials(currentUser.id, {
        hiteam_email: hiteamEmail.trim(),
        hiteam_password: hiteamPassword.trim(),
      });
      toast.success('HiTeam credentials saved!');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={isMandatory ? undefined : onOpenChange}>
      <DialogContent className="max-w-sm" onInteractOutside={isMandatory ? (e) => e.preventDefault() : undefined} hideClose={isMandatory}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <Key className="w-5 h-5" /> {isMandatory ? 'HiTeam Credentials Required' : 'Setup HiTeam Credentials'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {isMandatory ? (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700">
                You must save your HiTeam login credentials to continue using the app. These are required for sales point validation.
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-600">
              To validate sales points, please enter your HiTeam login credentials. You can update these anytime from your profile.
            </p>
          )}
          <div>
            <Label className="text-xs text-slate-600">HiTeam User ID (Email)</Label>
            <Input
              data-testid="setup-hiteam-email"
              type="email"
              value={hiteamEmail}
              onChange={e => setHiteamEmail(e.target.value)}
              placeholder="your.email@example.com"
              className="mt-1 h-9"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">HiTeam Password</Label>
            <div className="relative mt-1">
              <Input
                data-testid="setup-hiteam-password"
                type={showPassword ? 'text' : 'password'}
                value={hiteamPassword}
                onChange={e => setHiteamPassword(e.target.value)}
                placeholder="Enter password"
                className="h-9 pr-9"
              />
              <Button variant="ghost" size="sm" onClick={() => setShowPassword(!showPassword)} className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0">
                {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            {!isMandatory && (
              <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Skip for now</Button>
            )}
            <Button
              data-testid="setup-save-hiteam-btn"
              onClick={handleSave}
              disabled={saving || !hiteamEmail.trim() || !hiteamPassword.trim()}
              className={`${isMandatory ? 'w-full' : 'flex-1'} gap-1.5 bg-amber-600 hover:bg-amber-700 text-white`}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Validating...' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export function PasswordSetupPrompt({ open, onOpenChange, isMandatory = false }) {
  const { currentUser, setCurrentUser, api } = useApp();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!password.trim() || password.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setSaving(true);
    try {
      await api.setUserPassword(currentUser.id, password.trim());
      // Update currentUser state so the prompt never re-appears
      setCurrentUser(prev => ({ ...prev, has_password: true }));
      localStorage.removeItem('password_setup_dismissed');
      toast.success('Panel password saved! You can now login with your password.');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save password');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={isMandatory ? undefined : onOpenChange}>
      <DialogContent className="max-w-sm" onInteractOutside={isMandatory ? (e) => e.preventDefault() : undefined} hideClose={isMandatory}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <Lock className="w-5 h-5" /> {isMandatory ? 'Panel Password Required' : 'Set Panel Password'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {isMandatory ? (
            <p className="text-sm text-slate-600">
              You must create a panel password to continue. This enables faster login next time.
            </p>
          ) : (
            <p className="text-sm text-slate-600">
              Create a panel password for faster login next time. You won't need OTP or HiTeam credentials.
            </p>
          )}
          <div>
            <Label className="text-xs text-slate-600">New Password</Label>
            <Input
              data-testid="setup-password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter password (min 4 characters)"
              className="mt-1 h-9"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Confirm Password</Label>
            <Input
              data-testid="setup-confirm-password"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              className="mt-1 h-9"
            />
          </div>
          <div className="flex gap-2 pt-1">
            {!isMandatory && (
              <Button variant="outline" size="sm" className="flex-1" onClick={() => onOpenChange(false)}>
                Skip for now
              </Button>
            )}
            <Button size="sm" className={`${isMandatory ? 'w-full' : 'flex-1'} bg-amber-600 hover:bg-amber-700 text-white`} onClick={handleSave} disabled={saving || !password || !confirmPassword}>
              {saving ? 'Saving...' : 'Save Password'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
